import {
  BodyText,
  Field,
  FieldError,
  FieldLabel,
  MultiSelectSearch,
  RadioGroup,
  Textarea,
  Tooltip,
  TooltipTrigger,
} from "@/components/ui";
import {
  type Icd10CodeSearchResult,
  useIcd10Search,
} from "@/network/useIcd10Search";

import {
  type LabNetwork,
  RequestGroup,
  type ServiceRequest,
  getCodeValue,
  getCoding,
} from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { CircleQuestionMark } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import type {
  LabOrderEntryActionSummary,
  LabOrderEntryStatusSummary,
} from "../network/useLabOrdersByEncounter";
import { useUpdateLabOrder } from "../network/useUpdateLabOrder";
import { PSCSelection } from "./PSCSelection";
import { type OrderFormData, orderFormSchema } from "./schemas";
import { useOrderFormOptions } from "./useOrderFormOptions";

const MAX_ICD10_CODES = 10;

export const OrderForm = ({
  readOnly,
  patientId,
  encounterId,
  requestGroup,
  serviceRequests,
  // @ts-ignore TS6133 im gonna need this soon
  statusSummary,
  actionSummary,
}: {
  readOnly: boolean;
  patientId: string;
  encounterId: string;
  requestGroup: RequestGroup;
  serviceRequests: ServiceRequest[];
  statusSummary: LabOrderEntryStatusSummary;
  actionSummary: LabOrderEntryActionSummary;
}) => {
  const [icd10SearchTerm, setIcd10SearchTerm] = useState("");
  const previousTextareaValue = useRef<string>(requestGroup.orderComment || "");
  const requestGroupVersion = useRef<string>(
    requestGroup.meta?.versionId || "",
  );

  const { billToOptions, networkOptions, changeNetworkDisabledReason } =
    useOrderFormOptions({
      patientId,
      requestGroup,
      serviceRequests,
      selectedNetwork: requestGroup.labNetwork,
      selectedBillingMethod: requestGroup.billingMethod,
    });

  const { data: icd10Codes, isFetching: isSearchingCodes } = useIcd10Search({
    searchText: icd10SearchTerm,
    nested: false,
  });

  const updateLabOrder = useUpdateLabOrder({
    patientId,
    encounterId,
    requestGroupId: requestGroup.id as string,
  });

  const form = useForm<OrderFormData>({
    resolver: zodResolver(orderFormSchema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      network: requestGroup.labNetwork,
      billTo: requestGroup.billingMethod,
      orderNote: requestGroup.orderComment ?? "",
      icd10Codes: requestGroup.icd10Codes.map((code) => ({
        code: getCodeValue(code, RequestGroup.ICD10_SYSTEM),
        display:
          code.text || getCoding(code, RequestGroup.ICD10_SYSTEM)?.display,
        version: getCoding(code, RequestGroup.ICD10_SYSTEM)?.version,
      })),
      compendiumId: requestGroup.orderCompendiumSource,
      tests: serviceRequests.reduce(
        (acc, serviceRequest) => {
          acc[serviceRequest.id as string] = {
            reminderDate: serviceRequest?.reminderDate
              ? new Date(`${serviceRequest.reminderDate}T00:00:00`)
              : new Date(),
            testNote: serviceRequest.testComment ?? "",
          };
          return acc;
        },
        {} as Record<string, { reminderDate: Date; testNote: string }>,
      ),
    },
  });

  // Force a trigger on form initialization to ensure the ICD-10 warning
  // displays immediately
  useEffect(() => {
    form.trigger();
  }, [form]);

  const resetForm = useCallback(() => {
    form.reset({
      network: requestGroup.labNetwork,
      billTo: requestGroup.billingMethod,
      orderNote: requestGroup.orderComment ?? "",
      icd10Codes: requestGroup.icd10Codes.map((code) => ({
        code: getCodeValue(code, RequestGroup.ICD10_SYSTEM),
        display:
          code.text || getCoding(code, RequestGroup.ICD10_SYSTEM)?.display,
        version: getCoding(code, RequestGroup.ICD10_SYSTEM)?.version,
      })),
    });
    form.trigger("icd10Codes");
  }, [form, requestGroup]);

  useEffect(() => {
    if (requestGroup.meta?.versionId !== requestGroupVersion.current) {
      requestGroupVersion.current = requestGroup.meta?.versionId || "";
      resetForm();
    }
  }, [requestGroup.meta?.versionId, resetForm]);

  const updateWithFailureReset = useCallback(async () => {
    try {
      await updateLabOrder.mutateAsync({
        labNetwork: form.getValues("network"),
        billingMethod: form.getValues("billTo"),
        icd10Codes: form.getValues("icd10Codes"),
        orderNote: form.getValues("orderNote"),
      });
    } catch (error) {
      resetForm();
    }
  }, [form, updateLabOrder, resetForm]);

  const handleNetworkChange = async (labNetwork: LabNetwork) => {
    if (
      labNetwork === RequestGroup.LAB_NETWORKS.LABCORP &&
      form.getValues("billTo") === RequestGroup.BILLING_METHODS.CLIENT_PAY
    ) {
      form.setValue("billTo", RequestGroup.BILLING_METHODS.SELF_PAY, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    await updateWithFailureReset();
  };

  return (
    <div>
      <form className="grid gap-2">
        <Controller
          control={form.control}
          name="network"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="network" className="gap-0.5 flex">
                Lab Network
              </FieldLabel>
              <RadioGroup
                id="network"
                name={`${requestGroup.id}-network-v2`}
                onValueChange={(v) => {
                  field.onChange(v);
                  handleNetworkChange(v as LabNetwork);
                }}
                options={networkOptions}
                orientation="horizontal"
                value={field.value}
                disabled={readOnly || Boolean(changeNetworkDisabledReason)}
                aria-invalid={fieldState.invalid}
              />
            </Field>
          )}
        />
        <Controller
          control={form.control}
          name="billTo"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="billTo" className="gap-0.5 flex">
                Bill To
                <Tooltip
                  triggerClassName="flex ml-0.5 items-center"
                  tooltipContentComponent={
                    <div className="flex flex-col gap-1 py-1">
                      <BodyText size="sm" textColor="white">
                        Insurance: The patient's insurance will be billed by the
                        lab.
                      </BodyText>
                      <BodyText size="sm" textColor="white">
                        Curai-Pay: The patient will pay Curai at a contracted
                        rate.
                      </BodyText>
                      <BodyText size="sm" textColor="white">
                        Self-Pay: The patient will pay out-of-pocket at the lab.
                      </BodyText>
                    </div>
                  }
                >
                  <TooltipTrigger className="h-fit">
                    <CircleQuestionMark className="w-3 h-3" color="gray" />
                  </TooltipTrigger>
                </Tooltip>
              </FieldLabel>
              <RadioGroup
                id="billTo"
                name={`${requestGroup.id}-bill-to-v2`}
                value={field.value}
                onValueChange={async (v) => {
                  field.onChange(v);
                  await updateWithFailureReset();
                }}
                options={billToOptions}
                disabled={readOnly}
                orientation="horizontal"
                aria-invalid={fieldState.invalid}
              />
            </Field>
          )}
        />
        <PSCSelection
          readOnly={readOnly}
          needsPSCSelection={!actionSummary.compendiumSatisfied}
          patientId={patientId}
          encounterId={encounterId}
          requestGroup={requestGroup}
        />
        <Controller
          control={form.control}
          name="icd10Codes"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel>
                {requestGroup.billingMethod ===
                  RequestGroup.BILLING_METHODS.INSURANCE && "*"}
                ICD-10 Codes
              </FieldLabel>
              <FieldError errors={[fieldState.error]} />
              <MultiSelectSearch
                placeholder="Search ICD-10 Codes"
                selectedItems={field.value?.map((code) => ({
                  label: `${code.code} - ${code.display}`,
                  shortenedLabel: code.display,
                  value: code.code,
                  metadata: code,
                }))}
                noResultsText={
                  form.getValues("icd10Codes")?.length === MAX_ICD10_CODES
                    ? `A maximum of ${MAX_ICD10_CODES} ICD-10 codes are allowed.`
                    : "No results found"
                }
                options={
                  icd10Codes?.map((code) => ({
                    label: `${code.id} - ${code.term}`,
                    value: code.id,
                    metadata: code,
                  })) || []
                }
                onInputChange={(text) => {
                  setIcd10SearchTerm(text);
                }}
                onItemAdded={async (item) => {
                  const newCodes = [
                    ...form.getValues("icd10Codes"),
                    {
                      code: (item.metadata as Icd10CodeSearchResult).id,
                      display: (item.metadata as Icd10CodeSearchResult).term,
                      version: (item.metadata as Icd10CodeSearchResult).version,
                    },
                  ];

                  form.setValue("icd10Codes", newCodes, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  await updateWithFailureReset();
                }}
                onItemRemoved={async (item) => {
                  const newCodes = form
                    .getValues("icd10Codes")
                    .filter((code) => code.code !== item.value);

                  form.setValue("icd10Codes", newCodes, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  await updateWithFailureReset();
                }}
                disabled={readOnly}
                minLength={2}
                isLoading={isSearchingCodes}
                showLoadingSpinner={false}
                openOnFocus
              />
            </Field>
          )}
        />
        <Controller
          control={form.control}
          name="orderNote"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="orderNote">Notes for Lab Tech</FieldLabel>
              <Textarea
                {...field}
                id="orderNote"
                name={`${requestGroup.id}-order-note-v2`}
                value={field.value}
                onFocus={() => {
                  previousTextareaValue.current = field.value || "";
                }}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={async () => {
                  if (field.value === previousTextareaValue.current) {
                    return;
                  }
                  await updateWithFailureReset();
                }}
                disabled={readOnly}
                aria-invalid={fieldState.invalid}
              />
            </Field>
          )}
        />
      </form>
    </div>
  );
};
